/**
 * RecommendationsTab — the "Recommendations" tab of Guest Experience.
 *
 * Default: the handoff's clean, category-filtered "Curated" card grid, wired to
 * real places (apiClient.getPlaces). An "Edit / Curate" toggle reveals the full
 * PlacesPage editor + AI-research engine inline — preserving its
 * research.batch_research plan gate. Demo-lock is enforced by the route's
 * DemoGuard wrapper.
 */

import { useEffect, useMemo, useState } from "react"
import { Sparkles, MapPin, Pencil, ArrowLeft } from "lucide-react"
import { apiClient } from "@/lib/api"
import { usePropertyContext } from "@/lib/usePropertyContext"
import { cn } from "@/lib/utils"
import { PlacesPage } from "@/pages/PlacesPage"

interface PlaceLike {
  id: string
  area?: string
  type?: string
  en?: Record<string, unknown>
}

type Bucket = "eat" | "see" | "coffee"

const TYPE_BUCKET: Record<string, Bucket> = {
  restaurant: "eat",
  bar: "eat",
  cafe: "coffee",
  museum: "see",
  park: "see",
  beach: "see",
  attraction: "see",
  nature: "see",
  activity: "see",
  shop: "see",
  other: "see",
}

const BUCKET_META: Record<Bucket, { label: string; text: string; bg: string }> = {
  eat: { label: "Eat", text: "text-rose-strong", bg: "bg-rose-soft" },
  see: { label: "See", text: "text-sky", bg: "bg-sky-soft" },
  coffee: { label: "Coffee", text: "text-amber-strong", bg: "bg-amber-soft" },
}

const FILTERS: { key: "all" | Bucket; label: string }[] = [
  { key: "all", label: "All" },
  { key: "eat", label: "Eat" },
  { key: "see", label: "See" },
  { key: "coffee", label: "Coffee" },
]

const str = (v: unknown) => (typeof v === "string" ? v : "")

export function RecommendationsTab() {
  const { propertyId, property } = usePropertyContext()
  const [places, setPlaces] = useState<PlaceLike[]>([])
  const [loading, setLoading] = useState(true)
  const [cat, setCat] = useState<"all" | Bucket>("all")
  const [editing, setEditing] = useState(false)

  useEffect(() => {
    if (!propertyId) return
    let cancelled = false
    setLoading(true)
    apiClient
      .getPlaces(propertyId)
      .then((res) => {
        if (cancelled) return
        setPlaces(res.ok && Array.isArray(res.places) ? (res.places as unknown as PlaceLike[]) : [])
      })
      .catch(() => !cancelled && setPlaces([]))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [propertyId])

  const filtered = useMemo(() => {
    if (cat === "all") return places
    return places.filter((p) => TYPE_BUCKET[str(p.type)] === cat)
  }, [places, cat])

  if (editing) {
    return (
      <div>
        <button
          type="button"
          onClick={() => setEditing(false)}
          className="mb-4 inline-flex items-center gap-1.5 rounded-[9px] border border-border bg-card px-3 py-2 text-[12.5px] font-semibold text-ink-2 transition-colors hover:bg-line-soft"
        >
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
          Back to recommendations
        </button>
        <PlacesPage />
      </div>
    )
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setCat(f.key)}
              className={cn(
                "rounded-full border px-3.5 py-1.5 text-[12px] font-semibold transition-all",
                cat === f.key
                  ? "border-foreground bg-foreground text-white"
                  : "border-border bg-card text-ink-2 hover:border-line-2",
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2.5">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-soft px-2.5 py-1 text-[11.5px] font-semibold text-brand-strong">
            <Sparkles className="h-3 w-3 fill-current" strokeWidth={0} />
            Curated for {property?.name || "this property"}
          </span>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="inline-flex items-center gap-1.5 rounded-[9px] border border-border bg-card px-3 py-1.5 text-[12px] font-semibold text-ink-2 transition-colors hover:bg-line-soft"
          >
            <Pencil className="h-3.5 w-3.5" strokeWidth={1.9} />
            Edit / Curate
          </button>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-[150px] animate-pulse rounded-card border border-border bg-line-soft" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-card border border-dashed border-line-2 bg-card px-6 py-16 text-center">
          <MapPin className="h-8 w-8 text-muted-foreground" strokeWidth={1.6} />
          <p className="mt-3 text-[14px] font-semibold text-foreground">No places yet</p>
          <p className="mt-1 max-w-sm text-[12.5px] text-muted-foreground">
            Add local recommendations and let AI research enrich them for your guests.
          </p>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="mt-4 inline-flex items-center gap-1.5 rounded-[9px] bg-forest px-3.5 py-2 text-[12.5px] font-semibold text-white transition-opacity hover:opacity-90"
          >
            <Pencil className="h-3.5 w-3.5" strokeWidth={2} />
            Curate places
          </button>
        </div>
      ) : (
        <section className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((p) => {
            const bucket = TYPE_BUCKET[str(p.type)] || "see"
            const meta = BUCKET_META[bucket]
            const name = str(p.en?.name) || p.id
            const note = str(p.en?.short) || str(p.en?.description)
            return (
              <article
                key={p.id}
                className="rounded-card border border-border bg-card p-[18px] shadow-sm transition-all hover:border-line-2 hover:shadow-md"
              >
                <div className="mb-2.5 flex items-start justify-between">
                  <span
                    className={cn(
                      "inline-flex items-center rounded-full px-2.5 py-0.5 text-[10.5px] font-semibold uppercase tracking-[0.04em]",
                      meta.text,
                      meta.bg,
                    )}
                  >
                    {meta.label}
                  </span>
                </div>
                <h4 className="text-[15.5px] font-semibold text-foreground" style={{ fontWeight: 650 }}>
                  {name}
                </h4>
                {note && <p className="mt-1.5 line-clamp-3 text-[12.5px] leading-[1.55] text-ink-2">{note}</p>}
                {p.area && (
                  <div className="mt-3 flex items-center gap-1.5 border-t border-line-soft pt-3 text-[11.5px] font-medium text-muted-foreground">
                    <MapPin className="h-[13px] w-[13px]" strokeWidth={1.8} />
                    {p.area}
                  </div>
                )}
              </article>
            )
          })}
        </section>
      )}
    </div>
  )
}
